import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, Activity, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

export default function ProgressPenilaianPage() {
  const [kelasList, setKelasList] = useState([]);
  const [taList, setTaList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [jenisUjianList, setJenisUjianList] = useState([]);
  const [santriCounts, setSantriCounts] = useState({});
  const [nilaiList, setNilaiList] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [kelasId, setKelasId] = useState("");
  const [tahunAjaran, setTahunAjaran] = useState("");
  const [semester, setSemester] = useState("ganjil");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [k, ta, m, j] = await Promise.all([
        api.get("/kelas?limit=500"),
        api.get("/tahun-ajaran"),
        api.get("/mata-pelajaran"),
        api.get("/jenis-ujian"),
      ]);
      setKelasList(k.data.items || []);
      setTaList(ta.data.items || []);
      setMapelList(m.data.items || []);
      setJenisUjianList(j.data.items || []);
      const active = (ta.data.items || []).find(t => t.is_active);
      if (active) { setTahunAjaran(active.tahun_ajaran); setSemester(active.semester); }
    })();
  }, []);

  useEffect(() => {
    if (!kelasId || !tahunAjaran) return;
    (async () => {
      setLoading(true);
      try {
        const [n, s, a] = await Promise.all([
          api.get(`/nilai/list?kelas_id=${kelasId}&tahun_ajaran=${tahunAjaran}&semester=${semester}`),
          api.get(`/santri?kelas_id=${kelasId}&status=aktif&limit=500`),
          api.get(`/assignments?role_type=guru_mapel&kelas_id=${kelasId}&tahun_ajaran=${tahunAjaran}&semester=${semester}`),
        ]);
        setNilaiList(n.data.items || []);
        setSantriCounts({ [kelasId]: (s.data.total || (s.data.items || []).length) });
        setAssignments(a.data.items || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [kelasId, tahunAjaran, semester]);

  // Compute progress per mata pelajaran (mapel yang diassign untuk kelas ini)
  const progressData = useMemo(() => {
    if (!kelasId || !assignments.length) return [];
    const totalSantri = santriCounts[kelasId] || 0;
    return assignments.map(a => {
      const mapelName = a.mata_pelajaran?.nama || "?";
      const mapelId = a.mata_pelajaran_id;
      const ustadzName = a.ustadz?.nama || "?";
      // Nilai untuk mapel ini di kelas ini
      const mapelNilai = nilaiList.filter(n => n.mata_pelajaran_id === mapelId);
      const santriDinilai = new Set(mapelNilai.map(n => n.santri_id)).size;
      let status = "belum";
      let statusLabel = "Belum Diisi";
      let color = "red";
      if (santriDinilai === 0) { status = "belum"; }
      else if (santriDinilai < totalSantri) { status = "partial"; statusLabel = "Belum Lengkap"; color = "amber"; }
      else { status = "selesai"; statusLabel = "Selesai"; color = "green"; }
      return { mapelId, mapelName, ustadzName, santriDinilai, totalSantri, status, statusLabel, color };
    });
  }, [assignments, nilaiList, santriCounts, kelasId]);

  const totalMapel = progressData.length;
  const selesai = progressData.filter(p => p.status === "selesai").length;
  const percent = totalMapel ? Math.round((selesai / totalMapel) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Activity className="h-6 w-6 text-emerald-600" /> Progress Penilaian</h1>
        <p className="text-sm text-slate-500">Monitor kelengkapan pengisian nilai per mata pelajaran.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={tahunAjaran || "none"} onValueChange={(v) => setTahunAjaran(v === "none" ? "" : v)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tahun Ajaran" /></SelectTrigger>
          <SelectContent>
            {taList.map(t => <SelectItem key={t.id} value={t.tahun_ajaran}>{t.tahun_ajaran}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={semester} onValueChange={setSemester}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ganjil">Ganjil</SelectItem>
            <SelectItem value="genap">Genap</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kelasId || "none"} onValueChange={(v) => setKelasId(v === "none" ? "" : v)}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Pilih Kelas" /></SelectTrigger>
          <SelectContent>
            {kelasList.map(k => <SelectItem key={k.id} value={k.id}>{k.nama} ({k.tingkatan})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : !kelasId ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Pilih kelas untuk melihat progress.</CardContent></Card>
      ) : totalMapel === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Belum ada assignment guru mata pelajaran untuk kelas ini.<br /><span className="text-xs">Buka menu ‘Penugasan Ustadz’ untuk menambahkan.</span></CardContent></Card>
      ) : (
        <>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Progress Penilaian</p>
                  <p className="text-4xl font-bold mt-1">{percent}%</p>
                  <p className="text-sm mt-1 opacity-90">{selesai} dari {totalMapel} mata pelajaran selesai</p>
                </div>
                <Activity className="h-16 w-16 opacity-25" />
              </div>
              <Progress value={percent} className="mt-4 bg-white/20" />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {progressData.map(p => {
              const Icon = p.status === "selesai" ? CheckCircle2 : p.status === "partial" ? AlertCircle : XCircle;
              const badgeCls = p.color === "green" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : p.color === "amber" ? "bg-amber-100 text-amber-700 border-amber-200"
                            : "bg-red-100 text-red-700 border-red-200";
              return (
                <Card key={p.mapelId}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-800">{p.mapelName}</p>
                        <p className="text-xs text-slate-500">{p.ustadzName}</p>
                        <p className="text-xs text-slate-500 mt-1">{p.santriDinilai}/{p.totalSantri} santri dinilai</p>
                      </div>
                      <Icon className={`h-5 w-5 ${p.color === "green" ? "text-emerald-500" : p.color === "amber" ? "text-amber-500" : "text-red-500"}`} />
                    </div>
                    <Badge variant="outline" className={`${badgeCls} mt-2 text-xs`}>{p.statusLabel}</Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
